// Validación con IA de los documentos pendientes ("Por validar"): un solo modal con
// tres pantallas encadenadas, para que el operador nunca pierda de vista qué está
// pasando con los documentos de la flota:
//
//   preview  → dry-run: cuántos documentos entran, de qué tipo, cuánto tarda y qué
//              hace (y qué NO hace) la IA. La acción primaria arranca el job.
//   progress → polling al progreso cada POLL_MS; se pausa con la pestaña oculta y
//              refresca al volver. Con un estado terminal baja el detalle.
//   result   → resumen + tabla por documento con lectura, cuadratura y decisión.
//
// El job vive en el servidor: cerrar el modal en progreso no lo cancela (se retoma
// desde el botón "Validando…" o desde el historial). Sin `items` y con `jobId` el
// modal abre directo en progreso o resultado (historial / job activo).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '../../../utils/toast';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, FileText, Sparkles } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Modal } from '../../../components/ui/Modal';
import { Skeleton } from '../../../components/ui/Skeleton';
import { Table, TBody, TD, TH, THead, TR } from '../../../components/ui/Table';
import {
  getAiValidation,
  getAiValidationProgress,
  previewAiValidation,
  startAiValidation,
} from '../../../api/billing';
import { billingDocLabel } from '../../../utils/billingDocTypes';
import { cn } from '../../../utils/cn';
import { formatCurrency, formatDate } from './utils/cobroHelpers';
import { dayLabel } from './utils/payrollWeeks';
import DocumentViewerModal from '../../../components/ui/DocumentViewerModal';

export const POLL_MS = 5000;
// Tope del polling: pasado esto se deja de consultar y se avisa (el job sigue en el
// servidor; el resultado queda en el historial).
export const POLL_TIMEOUT_MS = 25 * 60 * 1000;
const TERMINAL = new Set(['done', 'failed', 'cancelled']);

// Decisión por documento → badge del DS. `would_accept` es la aceptación SIMULADA
// del modo sombra: informa sin tocar el documento, por eso va en info y no en success.
const DECISION = {
  accepted: { tone: 'success', label: 'Aceptado' },
  would_accept: { tone: 'info', label: 'Aceptaría' },
  held: { tone: 'warning', label: 'Retenido' },
  needs_human: { tone: 'neutral', label: 'Para humano' },
  skipped_stale: { tone: 'muted', label: 'Omitido' },
  error: { tone: 'error', label: 'Error' },
  rollback_blocked: { tone: 'warning', label: 'No revertido' },
};

// Mensaje del backend o el genérico: el 403/503 traen motivo propio.
const apiMessage = (err, fallback) => err?.response?.data?.message ?? fallback;

// La función no está disponible por configuración: no hay reintento que valga.
const isUnavailable = (err) => [403, 503].includes(err?.response?.status);

// Códigos de `reasonDisabled` del backend → explicación para el operador. Un código
// desconocido se muestra tal cual antes que esconderlo.
export const REASON_DISABLED = {
  ia_no_configurada: 'La IA no está configurada para este operador',
  tenant_apagado: 'La validación con IA está apagada para este operador',
  pais_no_soportado: 'El país del operador todavía no está soportado',
  sin_rut_courier: 'El operador no tiene RUT registrado: no se puede verificar el receptor',
};

// Documentos ya resueltos por el job (para la barra). `noText` no suma: es un detalle
// de POR QUÉ un documento quedó para humano, no una decisión aparte.
export const processedCount = (c) =>
  ['accepted', 'wouldAccept', 'held', 'needsHuman', 'skippedStale', 'error']
    .reduce((sum, k) => sum + (Number(c?.[k]) || 0), 0);

// Lectura de duración "~2 min" / "~45 s" para el estimado del dry-run.
const estimateLabel = (seconds, t) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return null;
  if (s < 90) return t('~{{n}} s', { n: Math.round(s) });
  return t('~{{n}} min', { n: Math.max(1, Math.round(s / 60)) });
};

// Delta declarado − esperado de un item: primero el que calculó el backend, si no
// el que sale de las lecturas; null cuando no hay con qué comparar.
const itemDelta = (item) => {
  const backend = item?.checks?.monto?.delta;
  if (typeof backend === 'number' && Number.isFinite(backend)) return backend;
  const read = item?.readings?.deterministic?.montoTotal;
  const expected = item?.expected?.amount;
  if (typeof read === 'number' && typeof expected === 'number') return read - expected;
  return null;
};

// Título del chip "OCR + visión": con qué confianza leyó el OCR y qué modelo de visión
// se usó. En un lote activo, un ítem en modo sombra se leyó pero NO se aceptó: se dice.
const ocrTitle = (item, batchShadow, t) => {
  const confidence = Number(item?.ocr?.confidence);
  const base = t('Leído por OCR (confianza {{confidence}} %) y modelo de visión {{model}}', {
    confidence: Number.isFinite(confidence) ? Math.round(confidence) : '—',
    model: item?.vision?.model || item?.llm?.model || '—',
  });
  return item?.mode === 'shadow' && !batchShadow
    ? `${base} ${t('(escaneo en modo sombra: no se aceptó)')}`
    : base;
};

// Texto de "Escaneos" del estado del backend: OCR + visión (y en qué modo) o manual.
// Solo informa; nunca condiciona el botón ni el arranque.
export const scansLabel = (scans, t) => {
  if (!scans || typeof scans !== 'object') return null;
  if (!scans.enabled) return t('Escaneos: revisión manual');
  return scans.mode === 'shadow'
    ? t('Escaneos: OCR + visión (modo sombra)')
    : t('Escaneos: OCR + visión (modo activo)');
};

export default function AiValidationModal({
  open = true,
  stage: initialStage = 'preview',
  jobId: initialJobId = null,
  items = [],
  mode = 'active',
  scans = null,
  drivers = [],
  onClose,
  onFinished,
}) {
  const { t } = useTranslation();
  const [stage, setStage] = useState(initialStage);
  const [jobId, setJobId] = useState(initialJobId);

  // preview
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null); // { message, retryable }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  // progress
  const [progress, setProgress] = useState(null);
  const [pollFails, setPollFails] = useState(0);
  const [pollError, setPollError] = useState(null); // 4xx: el job no existe / sin permiso
  const [timedOut, setTimedOut] = useState(false);
  // result
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null); // { doc, driverName }

  const shadow = (result?.job?.mode ?? mode) === 'shadow';

  // ---------- preview (dry-run) ----------

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await previewAiValidation({ items });
      setPreview(res?.data ?? null);
    } catch (err) {
      setPreview(null);
      setPreviewError({
        message: apiMessage(err, t('No se pudo preparar la validación')),
        retryable: !isUnavailable(err),
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [items, t]);

  useEffect(() => {
    if (!open || stage !== 'preview') return;
    loadPreview();
  }, [open, stage, loadPreview]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await startAiValidation({ items });
      const id = res?.data?.jobId;
      // Un 202 sin jobId (proxy, body vacío) no es un job: no hay nada que seguir.
      if (!id) throw new Error('missing jobId');
      setJobId(id);
      setProgress(null);
      setStage('progress');
    } catch (err) {
      const status = err?.response?.status;
      const activeId = err?.response?.data?.jobId;
      if (status === 409 && activeId) {
        // Ya hay un job corriendo: el operador ve ESE en vez de un error seco.
        toast.info(t('Ya hay una validación en curso'));
        setJobId(activeId);
        setProgress(null);
        setStage('progress');
      } else if (status === 403 || status === 503) {
        // La función no está disponible para este operador: no hay nada que reintentar.
        toast.error(apiMessage(err, t('La validación con IA no está disponible')));
        onClose?.();
      } else {
        // 400 (semana abierta, país) u otro: el motivo del backend, con reintento (el
        // dry-run se vuelve a pedir por si la cola cambió).
        setPreviewError({ message: apiMessage(err, t('No se pudo iniciar la validación')), retryable: true });
      }
    } finally {
      setStarting(false);
    }
  };

  // ---------- result ----------

  const loadResult = useCallback(async (id) => {
    setResultLoading(true);
    setResultError(null);
    try {
      const res = await getAiValidation(id);
      setResult(res?.data ?? null);
      setStage('result');
    } catch (err) {
      setResultError(apiMessage(err, t('No se pudo cargar el resultado')));
    } finally {
      setResultLoading(false);
    }
  }, [t]);

  // Abierto directo en resultado (historial): baja el detalle sin pasar por el polling.
  useEffect(() => {
    if (!open || initialStage !== 'result' || !initialJobId) return;
    loadResult(initialJobId);
  }, [open, initialStage, initialJobId, loadResult]);

  // ---------- progress (polling) ----------

  useEffect(() => {
    if (!open || stage !== 'progress' || !jobId) return undefined;
    let cancelled = false;
    let finished = false;
    let intervalId = null;
    const startedAt = Date.now();
    setTimedOut(false);
    setPollFails(0);
    setPollError(null);

    const stop = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = null;
      document.removeEventListener('visibilitychange', onVisibility);
    };

    const tick = async () => {
      if (cancelled || finished) return;
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await getAiValidationProgress(jobId);
        if (cancelled || finished) return;
        const p = res?.data ?? null;
        setProgress(p);
        setPollFails(0);
        if (p && TERMINAL.has(p.status)) {
          finished = true;
          stop();
          loadResult(jobId);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        // 4xx (salvo 429) es definitivo —el job no existe o no hay permiso—: seguir
        // preguntando 25 minutos sería insistir a ciegas.
        if (status >= 400 && status < 500 && status !== 429) {
          finished = true;
          stop();
          setPollError(apiMessage(err, t('No se pudo consultar el avance de la validación')));
          return;
        }
        // Un fallo aislado no rompe nada: se reintenta en el próximo tick y se avisa
        // recién cuando se encadenan varios.
        setPollFails((n) => n + 1);
      }
      // El tope se evalúa DESPUÉS de consultar: al volver de una pestaña oculta largo
      // rato, el último GET puede traer el terminal en vez de un aviso de timeout.
      if (!cancelled && !finished && Date.now() - startedAt > POLL_TIMEOUT_MS) {
        finished = true;
        stop();
        setTimedOut(true);
      }
    };

    function onVisibility() {
      // Al volver a la pestaña se refresca al instante: si no, se vería un ciclo
      // entero con datos viejos.
      if (document.visibilityState === 'visible') tick();
    }

    tick();
    intervalId = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      stop();
    };
  }, [open, stage, jobId, loadResult, t]);

  // ---------- cierre ----------

  // Hay algo que refrescar en la vista (documentos aceptados, historial) cuando el job
  // ya terminó: desde el resultado, o desde el progreso si llegó al terminal aunque el
  // detalle no haya bajado (resultError) o se haya quedado en timeout.
  const jobEnded =
    stage === 'result' ||
    (stage === 'progress' && (timedOut || (progress != null && TERMINAL.has(progress.status))));

  const close = () => {
    if (starting) return;
    if (jobEnded) onFinished?.();
    onClose?.();
  };

  // ---------- derivados del resultado ----------

  const job = result?.job ?? null;
  const resultItems = useMemo(() => result?.items ?? [], [result]);
  const counters = job?.counters ?? progress?.counters ?? null;

  // Documento del summary para "Ver documento": mismo (driverId, weekStart) del item.
  const docFor = useCallback(
    (item) => {
      const d = drivers.find((x) => x.driverId === item.driverId);
      const doc = (d?.billingDocuments ?? []).find((x) => x.weekStart === item.weekStart);
      return doc ? { doc, driverName: item.driverName || d?.driverName || item.driverId } : null;
    },
    [drivers],
  );

  // ---------- render ----------

  const pendingN = Number(preview?.pending ?? 0);
  const canStart = !!preview?.enabled && pendingN > 0 && !previewLoading && !previewError;

  const title =
    stage === 'preview'
      ? shadow ? t('Simular validación con IA') : t('Validar con IA')
      : stage === 'progress'
        ? t('Validando documentos…')
        : t('Resultado de la validación');

  const description =
    stage === 'preview'
      ? t('Revisa qué se va a validar antes de confirmar.')
      : stage === 'progress'
        ? t('Puedes cerrar esta ventana: la validación sigue en el servidor y el resultado queda en el historial.')
        : job?.requestedByName
          ? t('Solicitada por {{name}}', { name: job.requestedByName })
          : undefined;

  const footer =
    stage === 'preview' ? (
      <>
        <Button variant="outline" onClick={close} disabled={starting}>{t('Cancelar')}</Button>
        <Button
          onClick={start}
          loading={starting}
          disabled={!canStart}
          iconLeft={<Sparkles className="w-4 h-4" />}
        >
          {shadow
            ? t('Simular {{n}} documentos', { n: pendingN })
            : t('Validar {{n}} documentos', { n: pendingN })}
        </Button>
      </>
    ) : stage === 'progress' ? (
      <Button variant="outline" onClick={close}>{t('Cerrar')}</Button>
    ) : (
      <Button onClick={close}>{t('Cerrar')}</Button>
    );

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        size={stage === 'result' ? 'xl' : 'md'}
        title={title}
        description={description}
        footer={footer}
        hideClose={starting}
      >
        {stage === 'preview' && (
          <PreviewBody
            t={t}
            shadow={shadow}
            loading={previewLoading}
            error={previewError}
            preview={preview}
            scans={scans}
            onRetry={loadPreview}
          />
        )}

        {stage === 'progress' && (
          <ProgressBody
            t={t}
            shadow={shadow}
            progress={progress}
            pollFails={pollFails}
            pollError={pollError}
            timedOut={timedOut}
            resultLoading={resultLoading}
            resultError={resultError}
            onRetryResult={() => loadResult(jobId)}
          />
        )}

        {stage === 'result' && (
          <ResultBody
            t={t}
            shadow={shadow}
            job={job}
            counters={counters}
            items={resultItems}
            loading={resultLoading}
            error={resultError}
            onRetry={() => loadResult(jobId)}
            docFor={docFor}
            onViewDoc={setViewerDoc}
          />
        )}
      </Modal>

      <DocumentViewerModal
        open={!!viewerDoc}
        url={viewerDoc?.doc?.url}
        filename={viewerDoc?.doc?.filename}
        title={viewerDoc?.doc?.filename || t('Documento')}
        description={
          viewerDoc?.doc
            ? t('{{name}} · semana {{from}} al {{to}}', {
                name: viewerDoc.driverName || '',
                from: formatDate(viewerDoc.doc.weekStart),
                to: formatDate(viewerDoc.doc.weekEnd),
              })
            : undefined
        }
        onClose={() => setViewerDoc(null)}
      />
    </>
  );
}

// ---------- pantallas ----------

function ErrorNote({ message, onRetry, t }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-border bg-bg-muted p-3.5" role="alert">
      <AlertTriangle className="w-4 h-4 text-status-error shrink-0 mt-0.5" aria-hidden />
      <div className="min-w-0 flex-1 flex flex-col gap-2">
        <span className="text-sm text-fg break-words">{message}</span>
        {onRetry && (
          <Button size="sm" variant="outline" onClick={onRetry} className="self-start">
            {t('Reintentar')}
          </Button>
        )}
      </div>
    </div>
  );
}

function PreviewBody({ t, shadow, loading, error, preview, scans, onRetry }) {
  if (loading) {
    return (
      <div className="flex flex-col gap-3" role="status" aria-busy="true" aria-label={t('Preparando la validación')}>
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (error) return <ErrorNote message={error.message} onRetry={error.retryable ? onRetry : undefined} t={t} />;
  if (!preview) return null;

  if (!preview.enabled) {
    return (
      <EmptyState
        icon={<Sparkles />}
        title={t('La validación con IA no está disponible')}
        description={preview.reasonDisabled
          ? t(REASON_DISABLED[preview.reasonDisabled] ?? preview.reasonDisabled)
          : t('Consulta con el administrador del operador.')}
      />
    );
  }

  const n = Number(preview.pending ?? 0);
  if (n === 0) {
    return (
      <EmptyState
        icon={<FileText />}
        title={t('No hay documentos pendientes para validar')}
        description={t('Todos los documentos de la cola ya fueron resueltos.')}
      />
    );
  }

  const by = preview.byDocType ?? {};
  const prev = preview.previous ?? {};
  const estimate = estimateLabel(preview.estimateSeconds, t);
  const scansText = scansLabel(scans, t);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-3xl font-semibold text-fg tabular-nums">{n}</span>
        <span className="text-sm text-fg-muted">{t('documentos pendientes')}</span>
        {estimate && (
          <span className="text-sm text-fg-subtle tabular-nums">{t('· {{time}} aprox.', { time: estimate })}</span>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm m-0 sm:grid-cols-3">
        <div className="flex flex-col">
          <dt className="text-xs text-fg-muted">{t('Facturas')}</dt>
          <dd className="text-fg tabular-nums m-0">{Number(by.factura ?? 0)}</dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-xs text-fg-muted">{t('Boletas')}</dt>
          <dd className="text-fg tabular-nums m-0">{Number(by.boleta ?? 0)}</dd>
        </div>
        {Number(by.null ?? 0) > 0 && (
          <div className="flex flex-col">
            <dt className="text-xs text-fg-muted">{t('Sin tipo')}</dt>
            <dd className="text-fg tabular-nums m-0">{Number(by.null)}</dd>
          </div>
        )}
      </dl>

      {(Number(prev.held ?? 0) > 0 || Number(prev.needsHuman ?? 0) > 0) && (
        <p className="m-0 text-xs text-fg-muted">
          {t('Con veredicto previo: {{held}} retenido(s), {{human}} para revisión humana. Se vuelven a evaluar.', {
            held: Number(prev.held ?? 0),
            human: Number(prev.needsHuman ?? 0),
          })}
        </p>
      )}

      <p className="m-0 rounded-lg border border-border bg-bg-muted p-3.5 text-sm text-fg">
        {t('Acepta solo los documentos cuyo folio, RUT, tipo y monto cuadran exactos; los demás quedan pendientes con su motivo. Nunca rechaza ni marca pagado.')}
      </p>

      {scansText && <p className="m-0 text-xs text-fg-muted">{scansText}</p>}

      {shadow && (
        <div className="flex items-start gap-2 text-xs text-fg-muted">
          <Badge tone="info" className="shrink-0">{t('Modo sombra')}</Badge>
          <span>{t('Es una simulación: informa qué aceptaría, pero no cambia ningún documento.')}</span>
        </div>
      )}
    </div>
  );
}

function ProgressBody({ t, shadow, progress, pollFails, pollError, timedOut, resultLoading, resultError, onRetryResult }) {
  const c = progress?.counters ?? {};
  const total = Number(c.total ?? 0);
  const done = Math.min(processedCount(c), total || Infinity);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const queued = progress?.status === 'queued' || !progress;
  // Sin total todavía la barra es indeterminada: sin valores numéricos (los lectores
  // anunciarían "0 de 0") y marcada como ocupada.
  const indeterminate = total === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-fg-muted">
            {queued ? t('En cola…') : t('Procesando documentos')}
          </span>
          <span className="font-medium text-fg tabular-nums">
            {total > 0 ? t('{{p}}/{{n}} documentos', { p: done, n: total }) : ''}
          </span>
        </div>
        <span
          className="block w-full h-2.5 rounded-full bg-bg-subtle overflow-hidden"
          role="progressbar"
          aria-valuenow={indeterminate ? undefined : done}
          aria-valuemin={indeterminate ? undefined : 0}
          aria-valuemax={indeterminate ? undefined : total}
          aria-busy={indeterminate || undefined}
          aria-label={t('Documentos procesados')}
        >
          <span
            className={cn(
              'block h-full rounded-full bg-brand-500 transition-[width] duration-300',
              queued && total === 0 && 'animate-pulse w-1/4',
            )}
            style={total > 0 ? { width: `${pct}%` } : undefined}
          />
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm m-0 sm:grid-cols-3" aria-live="polite">
        <Counter label={shadow ? t('Aceptaría') : t('Aceptados')} value={shadow ? c.wouldAccept : c.accepted} tone="text-status-success" />
        <Counter label={t('Retenidos')} value={c.held} tone="text-status-warning" />
        <Counter label={t('Para humano')} value={c.needsHuman} />
        <Counter label={t('Omitidos')} value={c.skippedStale} />
        <Counter label={t('Errores')} value={c.error} tone={Number(c.error) > 0 ? 'text-status-error' : undefined} />
      </dl>
      <OcrCount t={t} value={c.ocr} />

      {pollError && <ErrorNote message={pollError} t={t} />}

      {pollFails >= 3 && !timedOut && !pollError && (
        <p className="m-0 text-xs text-status-warning">
          {t('Sin respuesta del servidor; se sigue intentando.')}
        </p>
      )}

      {timedOut && (
        <ErrorNote
          t={t}
          message={t('La validación lleva más de 25 minutos. Sigue en el servidor: revisa el resultado más tarde en "Validaciones con IA".')}
        />
      )}

      {resultLoading && (
        <p className="m-0 text-xs text-fg-muted" role="status">{t('Cargando el resultado…')}</p>
      )}
      {resultError && <ErrorNote message={resultError} onRetry={onRetryResult} t={t} />}
    </div>
  );
}

function Counter({ label, value, tone }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className={cn('tabular-nums m-0 font-medium', tone ?? 'text-fg')}>{Number(value ?? 0)}</dd>
    </div>
  );
}

// Escaneos leídos por OCR + visión: detalle secundario (subconjunto de "sin capa de
// texto"), no una decisión: no entra en los contadores ni en la barra.
function OcrCount({ t, value }) {
  const n = Number(value ?? 0);
  if (!(n > 0)) return null;
  return <p className="m-0 -mt-2 text-xs text-fg-muted tabular-nums">{t('{{n}} leídos por OCR', { n })}</p>;
}

function ResultBody({ t, shadow, job, counters, items, loading, error, onRetry, docFor, onViewDoc }) {
  if (loading) {
    return (
      <div className="flex flex-col gap-3" role="status" aria-busy="true" aria-label={t('Cargando el resultado')}>
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (error) return <ErrorNote message={error} onRetry={onRetry} t={t} />;

  const c = counters ?? {};
  const failed = job?.status === 'failed' || job?.status === 'cancelled';

  return (
    <div className="flex flex-col gap-4">
      {failed && (
        <ErrorNote
          t={t}
          message={job.status === 'cancelled'
            ? t('La validación fue cancelada. Los documentos ya evaluados conservan su decisión.')
            : t('La validación terminó con error: {{error}}', {
              error: (typeof job.error === 'string' ? job.error : job.error?.message) || t('sin detalle'),
            })}
        />
      )}
      {job?.rolledBackAt && (
        <p className="m-0 text-xs text-fg-muted">
          {t('Este lote fue deshecho: sus aceptaciones volvieron a "Por validar".')}
        </p>
      )}

      <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm m-0">
        {shadow ? (
          <Counter label={t('Simulados (aceptaría)')} value={c.wouldAccept} tone="text-status-info" />
        ) : (
          <Counter label={t('Aceptados')} value={c.accepted} tone="text-status-success" />
        )}
        <Counter label={t('Retenidos')} value={c.held} tone="text-status-warning" />
        <Counter label={t('Para humano')} value={c.needsHuman} />
        <Counter label={t('Omitidos')} value={c.skippedStale} />
        <Counter label={t('Errores')} value={c.error} tone={Number(c.error) > 0 ? 'text-status-error' : undefined} />
      </dl>
      <OcrCount t={t} value={c.ocr} />

      {items.length === 0 ? (
        <EmptyState
          icon={<FileText />}
          title={t('Sin documentos en este lote')}
          description={t('El job no evaluó ningún documento.')}
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>{t('Repartidor')}</TH>
              <TH>{t('Tipo')}</TH>
              <TH>{t('Folio leído')}</TH>
              <TH className="text-right">{t('Monto leído / esperado')}</TH>
              <TH>{t('Decisión')}</TH>
              <TH>{t('Motivo')}</TH>
              <TH><span className="sr-only">{t('Documento')}</span></TH>
            </tr>
          </THead>
          <TBody>
            {items.map((item) => {
              const read = item.readings?.deterministic ?? {};
              const delta = itemDelta(item);
              const decision = DECISION[item.decision] ?? { tone: 'neutral', label: item.decision || '—' };
              const reasons = (Array.isArray(item.reasons) ? item.reasons : [])
                .map((r) => String(r ?? '').trim())
                .filter(Boolean);
              const target = docFor(item);
              return (
                <TR key={item.itemId ?? `${item.driverId}-${item.weekStart}`}>
                  <TD>
                    <span className="block font-medium text-fg truncate max-w-[200px]" title={item.driverName || item.driverId}>
                      {item.driverName || item.driverId}
                    </span>
                    {item.weekStart && (
                      <span className="block text-xs text-fg-muted tabular-nums">
                        {t('Semana del {{date}}', { date: dayLabel(item.weekStart) })}
                      </span>
                    )}
                  </TD>
                  <TD className="whitespace-nowrap">
                    {billingDocLabel(item.docType, t)}
                    {item.source === 'ocr' && (
                      <Badge tone="neutral" className="ml-1.5" title={ocrTitle(item, shadow, t)}>
                        {t('OCR + visión')}
                      </Badge>
                    )}
                  </TD>
                  <TD className="tabular-nums whitespace-nowrap">{read.folio ? `N°${read.folio}` : '—'}</TD>
                  <TD className="text-right tabular-nums whitespace-nowrap">
                    <span className="block text-fg">
                      {typeof read.montoTotal === 'number' ? formatCurrency(read.montoTotal) : '—'}
                    </span>
                    <span className="block text-xs text-fg-muted">
                      {typeof item.expected?.amount === 'number' ? formatCurrency(item.expected.amount) : '—'}
                      {delta !== null && (
                        <>
                          {' · '}
                          <span className={cn('font-medium', delta === 0 ? 'text-status-success' : 'text-status-error')}>
                            {delta === 0 ? t('cuadra') : `${delta > 0 ? '+' : '−'}${formatCurrency(Math.abs(delta))}`}
                          </span>
                        </>
                      )}
                    </span>
                  </TD>
                  <TD className="whitespace-nowrap">
                    <Badge tone={decision.tone} dot>{t(decision.label)}</Badge>
                  </TD>
                  <TD>
                    {reasons.length > 0 ? (
                      <span
                        className="block text-xs text-fg-muted max-w-[260px] truncate"
                        title={reasons.join(' · ')}
                      >
                        {reasons[0]}
                        {reasons.length > 1 && (
                          <span className="text-fg-subtle"> {t('(+{{n}})', { n: reasons.length - 1 })}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-fg-subtle">—</span>
                    )}
                  </TD>
                  <TD className="whitespace-nowrap text-right">
                    {target && (
                      <button
                        type="button"
                        onClick={() => onViewDoc(target)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
                      >
                        <FileText className="w-3.5 h-3.5" aria-hidden />
                        {t('Ver documento')}
                      </button>
                    )}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}
    </div>
  );
}
